import React, { memo } from 'react';
import { IBoxProps, Text, Box, ScrollView, Button, HStack, Spinner } from 'native-base';
import { Message } from './Message';
import { ComposeMessage } from './ComposeMessage';
import { useCustomerMessages } from '../hooks/useCustomerMessages';
import { View } from 'react-native';
import Loading from '../../Common/components/Loading';
import { LottieAnimation } from '../../Common/components/LottieAnimation';
import { isDoctorPortal } from '../../Common/utils/isDoctorPortal';

export const Messages = memo(
  ({
    ThreadID,
    ComposeBoxStyle,
    MarkReadWhenViewed,
  }: {
    ThreadID: string;
    ComposeBoxStyle?: IBoxProps;
    MarkReadWhenViewed?: boolean;
  }) => {
    const {
      messages,
      brand,
      customerId,
      channel,
      onType,
      scrollToBottom,
      showMarkAsRead,
      loading,
      loadingMore,
      hasMore,
      loadOlderMessages,
      transformSenderImage,
      markAsRead,
      refetch,
      typing,
      transformFrom,
      messagesEndRef,
    } = useCustomerMessages(ThreadID, MarkReadWhenViewed);

    const handleScroll = (event: any) => {
      const y = event.nativeEvent?.contentOffset?.y ?? 0;
      if (y < 80 && hasMore && !loadingMore) {
        loadOlderMessages();
      }
    };

    const messageList = messages.map((message) => {
      return (
        <Message
          key={message?.id}
          ThreadID={ThreadID}
          html={message?.html}
          channel={message?.channel ?? 'app'}
          files={message?.files ?? []}
          title={message?.title}
          messageId={message?.id ?? ''}
          refetch={refetch}
          sender={transformFrom(message?.senderID ?? '')}
          isCustomer={message?.senderID === ThreadID}
          message={message?.message ?? ''}
          sent={message?.sent}
          scrollToBottom={scrollToBottom}
          senderImage={transformSenderImage(message?.senderID ?? '')}
          type={message?.type ?? ''}
          senderID={message?.senderID ?? ''}
          canEdit={channel === 'app'}
          options={message?.options ?? []}
        />
      );
    });

    return (
      <View style={{ flex: 1, maxHeight: '100%' }}>
        <ScrollView
          ref={messagesEndRef}
          onScroll={handleScroll}
          scrollEventThrottle={200}
        >
          {loadingMore && (
            <HStack justifyContent="center" py={3}>
              <Spinner size="sm" color="gray.400" />
            </HStack>
          )}

          {!loadingMore && hasMore && (
            <HStack justifyContent="center" py={2}>
              <Text color="gray.400" fontSize="xs">Scroll up for older messages</Text>
            </HStack>
          )}

          {messageList}

          {!loading && messageList.length === 0 && (
            <Box flex={1} alignItems={'center'} justifyItems={'center'}>
              <Text color={'gray.400'}>No messages</Text>
            </Box>
          )}

          {loading && <Loading />}

          {typing && (
            <HStack space={2}>
              {transformSenderImage('platform')}
              <LottieAnimation
                loop={true}
                style={{ maxHeight: 50 }}
                source={require('./typing.json')}
              />
            </HStack>
          )}
        </ScrollView>

        {showMarkAsRead && (
          <Button mt={2} size={'sm'} onPress={markAsRead}>
            Mark as read
          </Button>
        )}

        {!isDoctorPortal() && (
          <Text mt={2} color="gray.500" fontSize={'xs'}>
            Please treat our team with respect. Remember, this is not a live chat with your doctor -
            we respond to messages as soon as we can.
          </Text>
        )}

        <ComposeMessage
          ComposeBoxStyle={ComposeBoxStyle}
          ThreadChannel={channel}
          CustomerID={customerId}
          Brand={brand}
          onType={onType}
          onSent={refetch}
          ThreadID={ThreadID}
        />
      </View>
    );
  },
  (oldProps, newProps) => oldProps.ThreadID === newProps.ThreadID,
);
